import axios from 'axios';

/**
 * List published CMS pages.
 *
 * @param {Object} options
 * @param {String} [options.locale] - Locale to list, defaults to `en` server side
 * @returns {Promise<Array>}
 */
export async function getPages({ locale } = {}) {
  const { data } = await axios.get('/api/cms/pages', {
    params: locale ? { locale } : {},
  });
  return data;
}

/**
 * Get a single published CMS page.
 *
 * When the page has been renamed the response carries `movedFrom`, and
 * `slug` holds the canonical value.
 *
 * @param {String} slug - Page slug
 * @param {Object} options
 * @param {String} [options.locale] - Preferred locale, falls back to `en`
 * @returns {Promise<Object>}
 */
export async function getPage(slug, { locale } = {}) {
  const { data } = await axios.get(
    `/api/cms/pages/${encodeURIComponent(slug)}`,
    { params: locale ? { locale } : {} },
  );
  return data;
}

/**
 * List published blog posts, newest first, paginated.
 *
 * @param {Object} options
 * @param {String} [options.locale] - Preferred locale
 * @param {Number|String} [options.page] - 1-based page number
 * @param {Number|String} [options.limit] - Page size
 * @param {String} [options.tag] - Only posts carrying this tag
 * @returns {Promise<Object>} `{ items, total, page, limit, pages, tags, tag }`
 */
export async function getPosts({ locale, page, limit, tag, category } = {}) {
  const params = {};
  if (locale) params.locale = locale;
  if (page) params.page = page;
  if (limit) params.limit = limit;
  if (tag) params.tag = tag;
  if (category) params.category = category;

  const { data } = await axios.get('/api/cms/posts', { params });
  return data;
}

/**
 * Get a single published blog post.
 *
 * @param {String} slug - Post slug
 * @param {Object} options
 * @param {String} [options.locale] - Preferred locale, falls back to `en`
 * @returns {Promise<Object>}
 */
export async function getPost(slug, { locale } = {}) {
  const { data } = await axios.get(
    `/api/cms/posts/${encodeURIComponent(slug)}`,
    { params: locale ? { locale } : {} },
  );
  return data;
}

/**
 * The FAQ: ordered categories of published questions.
 *
 * @param {Object} options
 * @param {String} [options.locale] - Preferred locale, falls back to `en`
 * @returns {Promise<Object>} `{ locale, categories }`
 */
export async function getFaq({ locale } = {}) {
  const { data } = await axios.get('/api/cms/faq', {
    params: locale ? { locale } : {},
  });
  return data;
}

/**
 * List announcements that are currently active.
 *
 * @returns {Promise<Array>}
 */
export async function getAnnouncements() {
  const { data } = await axios.get('/api/cms/announcements');
  return data;
}

/**
 * In-flight/settled menu request, shared for the lifetime of the page.
 *
 * The menu is rendered in up to three places at once (header, footer, mobile
 * navigation), and without this each would issue its own request for the
 * same public payload on every page load.
 *
 * Module scope, so a full page load — which is how all these links navigate
 * — gets fresh data. Not a long-lived cache.
 */
let menuRequest = null;

/**
 * Published pages flagged for the site menu.
 *
 * @returns {Promise<Array>} Objects with `title` and `slug`
 */
export async function getMenu() {
  if (!menuRequest) {
    menuRequest = axios
      .get('/api/cms/menu')
      .then(response => response.data)
      .catch(error => {
        // Don't cache a failure: a later mount should be able to retry.
        menuRequest = null;
        throw error;
      });
  }

  return menuRequest;
}

/**
 * Public site settings.
 *
 * @returns {Promise<Object>}
 */
export async function getPublicSettings() {
  const { data } = await axios.get('/api/cms/settings');
  return data;
}

/**
 * Shared, page-lifetime footer menu request.
 *
 * The footer renders on every page and in two components (shared + home), so
 * without this each mount would refetch the same public payload.
 */
let footerMenuRequest = null;

/**
 * The footer menu, as ordered sections of links.
 *
 * @returns {Promise<Array>} `[{ name, items: [{ label, url }] }]`
 */
export async function getFooterMenu() {
  if (!footerMenuRequest) {
    footerMenuRequest = axios
      .get('/api/cms/footer-menu')
      .then(response => response.data)
      .catch(error => {
        footerMenuRequest = null;
        throw error;
      });
  }

  return footerMenuRequest;
}
